import { GridConfig } from "../GridConfig";
import { DiagramEventEmitter } from "../../../../diagram/modules/DiagramEventEmitter";
import { DIAGRAM_EMIT_EVENTS } from "../../../../diagram/modules/DiagramEvents";
import { GridSizedViewUtils } from "../../utils/GridSizeViewUtils";
import { CellHighlightHandler } from "../../utils/CellHighlightHandler";
import { PluginRetrievalUtils } from "../../utils/PluginRetrievalUtils";
import { CellRetrievalUtils } from "../../utils/CellRetrievalUtils";

/**
 * @descriptino Handles the expanse of a cell indepenednet plugin
 */
export class ExpandablePluginHandler {
    expandingPlugin = null;

    draggingFromCell = null;

    hoveredCells = [];

    lastExpandedToCell = null;

    fabricDiagramCtx;

    pluginHandler;

    cellRetrieval = new CellRetrievalUtils();

    instanceId;

    constructor(pluginHandler, instanceId) {
        this.pluginHandler = pluginHandler;
        this.instanceId = instanceId;
    }

    /**
     * @description start the expanding
     * @param {Object} plugin - the plugin instance that is being expanded
     * @param {Object} mousePos - current mouse position
     * @param {GridCell[][]} grid
     */
    triggeringExpandingPlugin(plugin, mousePos, grid) {
        this.expandingPlugin = plugin;
        this.draggingFromCell = this.cellRetrieval.getCell(this.cellRetrieval.getCellPosition(plugin.instance.position, null, grid), grid);
    }

    /**
     * @description canceles the expanding
     */
    cancelExpanding() {
        if (this.expandingPlugin && this.expandingPlugin.instance) {
            this.expandingPlugin.instance.clearExpanding();
            this.expandingPlugin.instance.expandingToCell(null);
        }
        this.expandingPlugin = null;
    }

    /**
     * @description handles the mouse up event
     * @param {CellIndependentPluginHandler} pluginHandler
     * @param {Object} pos  - current mouse position
     * @param {GridCell[][]} grid
     */
    mouseUp(pluginHandler, pos, grid) {
        this.expandingPlugin.instance.clearExpanding();
        const isInvalid = this.invalidDrop(pluginHandler, pos, grid);
        if (!this.lastExpandedToCell) {
            const position = {
                x: this.expandingPlugin.instance.position.x + this.expandingPlugin.instance.size.width - 5,
                y: this.expandingPlugin.instance.position.y + this.expandingPlugin.instance.size.height - 5
            };
            this.lastExpandedToCell = this.cellRetrieval.getCell(this.cellRetrieval.getCellPosition(position, null, grid), grid);
        }
        const oldSize = { ...this.expandingPlugin.instance.size };
        const newSize = this.getNewPluginSize(this.draggingFromCell, this.lastExpandedToCell);
        const newCoveredCells = pluginHandler.getCoveredCells(this.expandingPlugin.instance.position, newSize, grid, true);
        if (isInvalid) {
            this.resetDragHoverStatus();
            this.resetHandler(newCoveredCells);
            return;
        }
        const evEmitter = new DiagramEventEmitter(this.instanceId);
        const payload = { oldSize, newSize };

        evEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONTAINER_RESIZE, payload, true);
        this.expandingPlugin.instance.update({ size: newSize });
        const noLongerCoveredCells = this.expandingPlugin.coveredCells.filter(cell => newCoveredCells.indexOf(cell) < 0);
        this.expandingPlugin.coveredCells = newCoveredCells;
        this.expandingPlugin.coveredCellsAndAdjacent = pluginHandler.getCoveredCells(this.expandingPlugin.instance.position, newSize, grid, false);
        pluginHandler.markCellsAsVoidOrUnvoid(this.expandingPlugin.coveredCells, true);
        pluginHandler.markCellsAsVoidOrUnvoid(noLongerCoveredCells, false);
        this.expandingPlugin.instance.draw();
        this.resetHandler();
    }

    /**
     * @description resets the drag hover status on the cell independent plugins
     */
    resetDragHoverStatus() {
        const keys = this.pluginHandler.plugins.keys();
        for (const key of keys) {
            if (key === this.expandingPlugin.instance.uuid) continue;
            const plugin = this.pluginHandler.plugins.get(key);
            if (plugin.instance.state.dragHover) {
                plugin.instance.update({}, { dragHover: false });
                plugin.instance.draw();
            }
        }
    }

    /**
     * @description resets the expandable plugin handler
     */
    resetHandler(newCoveredCells) {
        this.expandingPlugin.instance.expandingToCell(null);
        this.expandingPlugin.instance.draw();
        this.expandingPlugin = null;
        this.draggingFromCell = null;
        this.lastExpandedToCell = null;
        this.hoveredCells = [];
        if (newCoveredCells) this.resetCells(newCoveredCells);
    }

    /**
     * @description sets the fabricDiagram instance
     * @param {FabricDiagram} ctx
     */
    setFabricDiagramContext(ctx) {
        this.fabricDiagramCtx = ctx;
    }

    /**
     * @description Returns the new plugin size after expanse
     * @param {GridCell} startCell
     * @param {GridCell} oldCell
     */
    getNewPluginSize(startCell, oldCell) {
        let width; let height; const
            gridConfig = new GridConfig(this.instanceId);
        width = (oldCell.layout.x - startCell.layout.x) + gridConfig.cellWidth;
        height = (oldCell.layout.y - startCell.layout.y) + gridConfig.cellHeight;
        return {
            width,
            height
        };
    }

    /**
     * @description Checks to see if is a invalid drop
     * @param {CellIndependentPluginHandler} pluginHandler
     * @param {Object} pos  - current mouse position
     * @param {GridCell[][]} grid
     */
    invalidDrop(pluginHandler, pos, grid) {
        const currentCell = this.cellRetrieval.getCell(this.cellRetrieval.getCellPosition(pos, null, grid), grid);
        if (!currentCell) return true;
        const newSize = this.getNewPluginSize(this.draggingFromCell, currentCell);
        const pluginRetrieval = new PluginRetrievalUtils();
        const pluginData = pluginRetrieval.getPluginFromDiagram({ pluginId: this.expandingPlugin.instance.data.id }, this.fabricDiagramCtx);
        const eventEmitter = new DiagramEventEmitter(this.instanceId);

        if (pluginData && pluginData.defaultSize && (newSize.width < pluginData.defaultSize.width || newSize.height < pluginData.defaultSize.height)) {
            eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.INVALID_CONTAINER_RESIZE, {
                REASON: "RESIZED_SMALLER_THEN_MINIMUM", MINIMUM_SIZE: pluginData.defaultSize, PLUGIN_ID: this.expandingPlugin.instance.data.id, PLUGIN_UUID: this.expandingPlugin.instance.uuid
            });
            return true;
        }
        const newCoveredCells = pluginHandler.getCoveredCells(this.expandingPlugin.instance.position, newSize, grid, true);

        for (let i = 0; i < newCoveredCells.length; i++) {
            const cell = newCoveredCells[i];
            if (cell.hasContent()) {
                eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.INVALID_CONTAINER_RESIZE, { REASON: "COVERED_FILLED_CELL", PLUGIN_ID: this.expandingPlugin.instance.data.id, PLUGIN_UUID: this.expandingPlugin.instance.uuid });
                return true;
            }
            if (cell.isVoid() && this.expandingPlugin.coveredCells.indexOf(cell) < 0) {
                eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.INVALID_CONTAINER_RESIZE, { REASON: "COVERED_CELL_INDEPENDENT_PLUGIN", PLUGIN_ID: this.expandingPlugin.instance.data.id, PLUGIN_UUID: this.expandingPlugin.instance.uuid });
                return true;
            }
        }
    }

    /**
     * @description Resets dragHover status of covered  cells during the expanse
     * @param {GridCell[]} cells
     */
    resetCells(cells) {
        const cellHighlight = new CellHighlightHandler(this.instanceId);
        cells.forEach(cell => {
            cellHighlight.updateDragHoverStatus(cell, false);
        });
    }

    /**
     * @description - handles the mouse move event
     * @param {Object} pos - current mouse pos
     * @param {Object} temporary - temporary grid config
     * @param {GridCell[][]} grid - generated grid matrix
     */
    mouseMove(pos, temporary, grid) {
        const currentCell = this.cellRetrieval.getCell(this.cellRetrieval.getCellPosition(pos, temporary, grid), grid);
        this.lastExpandedToCell = currentCell;
        if (!currentCell) return;
        const gridSizeUtils = new GridSizedViewUtils();
        let hoveredCells = gridSizeUtils.getGridCellsFromSubsection(this.draggingFromCell, currentCell, grid);
        hoveredCells = hoveredCells.filter(cell => this.expandingPlugin.coveredCells.indexOf(cell) === -1);
        this.highlightInvalidHoveredContent(hoveredCells);
        const cellsNoLongerHovered = this.hoveredCells.filter(cell => hoveredCells.indexOf(cell) < 0);
        const cellHighlight = new CellHighlightHandler(this.instanceId);
        cellsNoLongerHovered.forEach((cell) => {
            cellHighlight.updateDragHoverStatus(cell, false);
        });
        hoveredCells.forEach(cell => {
            cellHighlight.updateDragHoverStatus(cell, true);
        });
        this.hoveredCells = hoveredCells;
        this.expandingPlugin.instance.expandingToCell(currentCell);
    }

    /**
     * @description -
     * @param {GridCell[]} hoveredCells - invalid hovered cells
     */
    highlightInvalidHoveredContent(hoveredCells) {
        const keys = this.pluginHandler.plugins.keys();
        for (const key of keys) {
            if (key === this.expandingPlugin.instance.uuid) continue;
            const plugin = this.pluginHandler.plugins.get(key);
            const intersection = plugin.coveredCells.filter(cell => hoveredCells.indexOf(cell) > 0);
            if (intersection && intersection.length > 0) plugin.instance.update({}, { dragHover: true });
            else {
                plugin.instance.update({}, { dragHover: false });
                plugin.instance.draw();
            }
        }
    }

    /**
     * @description Checks to see if there is a expanding process undergoing
     */
    isExpanding() {
        return !!this.expandingPlugin;
    }

    /**
     * @description Updates hoveredCells draghover state value
     * @param {Boolean} draghoverVal - the value to set as dragHover on the provided cells
     */
    updateHoveredCells(draghoverVal) {
        this.hoveredCells.forEach(cell => {
            cell.update({}, { dragHover: draghoverVal });
            cell.draw();
        });
    }
}
